/**
 * LauncherPage Component
 *
 * Application launcher page where users can view and launch applications they have access to.
 * Features:
 * - Hierarchical grouping by menu/submenu
 * - Favorites functionality with star toggle
 * - Search across all applications
 * - Collapsible sections
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { Spinner } from '../components/ui/Spinner';
import { Badge } from '../components/ui/Badge';
import {
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  StarIcon as StarOutlineIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutProps } from '../hooks/useLayoutProps';
import { useApplications } from '../features/applications/hooks/useApplications';
import { useToggleFavorite } from '../features/users/hooks/useToggleFavorite';
import type { ApplicationSanitized } from '../types/application.types';
import { getErrorMessage } from '../lib/utils/errors';
import { ROUTES } from '../lib/constants';

interface GroupedApps {
  [menu: string]: {
    [submenu: string]: ApplicationSanitized[];
  };
}

export const LauncherPage: React.FC = () => {
  const { tenant, user } = useAuth();
  const layoutProps = useLayoutProps();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['Favorites']));
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set());

  const tenantId = tenant?.tenantId || '';
  const userId = user?.userId || '';

  // Fetch applications
  const { data: applications, isLoading, error } = useApplications(tenantId, { limit: 100 });

  // Toggle favorite mutation
  const toggleFavoriteMutation = useToggleFavorite({
    onSuccess: () => {
      console.log('Favorite toggled successfully');
    },
    onError: (error) => {
      console.error('Failed to toggle favorite:', error);
    },
  });

  // Get user's favorite application IDs
  const favoriteIds = useMemo(() => {
    return new Set(user?.favoriteApplications || []);
  }, [user?.favoriteApplications]);

  // Filter applications based on search and launcher visibility
  const filteredApps = useMemo(() => {
    if (!applications) return [];

    // First filter by showInLauncher - only show apps that should be visible in launcher
    // Default to true for backwards compatibility with existing apps
    let filtered = applications.filter((app) => app.showInLauncher !== false);

    if (!searchQuery) return filtered;

    const query = searchQuery.toLowerCase();
    return filtered.filter(
      (app) =>
        app.applicationName.toLowerCase().includes(query) ||
        (app.description && app.description.toLowerCase().includes(query)) ||
        (app.menu && app.menu.toLowerCase().includes(query)) ||
        (app.submenu && app.submenu.toLowerCase().includes(query))
    );
  }, [applications, searchQuery]);

  // Group applications by menu and submenu
  const groupedApps = useMemo(() => {
    const groups: GroupedApps = {};

    // Add favorites section if there are favorites
    const favoriteApps = filteredApps.filter((app) => favoriteIds.has(app.applicationId));
    if (favoriteApps.length > 0) {
      groups['Favorites'] = {
        'All Favorites': favoriteApps,
      };
    }

    // Group by menu/submenu
    filteredApps.forEach((app) => {
      const menu = app.menu || 'Uncategorized';
      const submenu = app.submenu || 'General';

      if (!groups[menu]) {
        groups[menu] = {};
      }
      if (!groups[menu][submenu]) {
        groups[menu][submenu] = [];
      }
      groups[menu][submenu].push(app);
    });

    return groups;
  }, [filteredApps, favoriteIds]);

  // Auto-expand all menus and submenus when data loads
  React.useEffect(() => {
    const allMenus = Object.keys(groupedApps);
    if (allMenus.length > 0) {
      // Expand all menus
      setExpandedMenus(new Set(allMenus));

      // Expand all submenus
      const allSubmenuKeys = new Set<string>();
      allMenus.forEach((menu) => {
        const submenus = groupedApps[menu];
        if (submenus) {
          Object.keys(submenus).forEach((submenu) => {
            allSubmenuKeys.add(`${menu}/${submenu}`);
          });
        }
      });
      setExpandedSubmenus(allSubmenuKeys);
    }
  }, [groupedApps]); // Only run when groupedApps changes

  const toggleMenu = (menu: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(menu)) {
        next.delete(menu);
      } else {
        next.add(menu);

        // Auto-expand all submenus when menu is expanded
        const submenus = groupedApps[menu];
        if (submenus) {
          setExpandedSubmenus((prevSubmenus) => {
            const nextSubmenus = new Set(prevSubmenus);
            Object.keys(submenus).forEach((submenu) => {
              nextSubmenus.add(`${menu}/${submenu}`);
            });
            return nextSubmenus;
          });
        }
      }
      return next;
    });
  };

  const toggleSubmenu = (key: string) => {
    setExpandedSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleFavorite = (app: ApplicationSanitized) => {
    const isFavorite = favoriteIds.has(app.applicationId);
    toggleFavoriteMutation.mutate({
      tenantId,
      userId,
      applicationId: app.applicationId,
      isFavorite,
    });
  };

  const handleLaunchApp = (app: ApplicationSanitized, e?: React.MouseEvent) => {
    const launchUrl = `${ROUTES.LAUNCH}/${app.applicationId}`;
    // Ctrl+Click or Cmd+Click opens in new tab
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(launchUrl);
    }
  };

  const handleLaunchAppNewTab = (app: ApplicationSanitized) => {
    window.open(`${ROUTES.LAUNCH}/${app.applicationId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Launcher</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>Launch and access your applications</p>
        </div>

        {/* Search */}
        <div className="max-w-md">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <Input
              type="search"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">Failed to load applications: {getErrorMessage(error)}</Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Grouped Applications */}
        {!isLoading && filteredApps && filteredApps.length > 0 && (
          <div className="space-y-4">
            {Object.entries(groupedApps).map(([menu, submenus]) => {
              const isMenuExpanded = expandedMenus.has(menu);

              return (
                <Card key={menu} className="overflow-hidden">
                  {/* Menu Header */}
                  <button
                    onClick={() => toggleMenu(menu)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-sunken transition-colors"
                  >
                    <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{menu}</h2>
                    {isMenuExpanded ? (
                      <ChevronDownIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                    )}
                  </button>

                  {/* Menu Content */}
                  {isMenuExpanded && (
                    <div className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                      {Object.entries(submenus).map(([submenu, apps]) => {
                        const submenuKey = `${menu}/${submenu}`;
                        const isSubmenuExpanded = expandedSubmenus.has(submenuKey) || submenu === 'All Favorites' || submenu === 'General';

                        return (
                          <div key={submenuKey} className="border-b last:border-b-0" style={{ borderColor: 'var(--color-border)' }}>
                            {/* Submenu Header (only show if not single submenu) */}
                            {Object.keys(submenus).length > 1 && (
                              <button
                                onClick={() => toggleSubmenu(submenuKey)}
                                className="w-full px-6 py-3 flex items-center justify-between hover:bg-surface-sunken transition-colors"
                                style={{ backgroundColor: 'var(--color-bg-sunken)' }}
                              >
                                <h3 className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{submenu}</h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{apps.length}</span>
                                  {isSubmenuExpanded ? (
                                    <ChevronDownIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                                  ) : (
                                    <ChevronRightIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                                  )}
                                </div>
                              </button>
                            )}

                            {/* Applications Grid */}
                            {isSubmenuExpanded && (
                              <div className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {apps.map((app) => {
                                  const isFavorite = favoriteIds.has(app.applicationId);

                                  return (
                                    <div
                                      key={app.applicationId}
                                      className="relative border rounded-lg p-4 hover:shadow-md transition-shadow"
                                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-raised)' }}
                                    >
                                      {/* Favorite Star Button */}
                                      <button
                                        onClick={() => handleToggleFavorite(app)}
                                        className="absolute top-2 right-2 p-1 rounded hover:bg-surface-sunken transition-colors"
                                        disabled={toggleFavoriteMutation.isPending}
                                      >
                                        {isFavorite ? (
                                          <StarSolidIcon className="h-5 w-5 text-yellow-500" />
                                        ) : (
                                          <StarOutlineIcon className="h-5 w-5 hover:text-yellow-500" style={{ color: 'var(--color-text-muted)' }} />
                                        )}
                                      </button>

                                      {/* App Icon */}
                                      <div className="flex items-center justify-center h-12 w-12 bg-primary-100 rounded-lg mb-3">
                                        <RocketLaunchIcon className="h-6 w-6 text-primary-600" />
                                      </div>

                                      {/* App Info */}
                                      <h4 className="text-base font-semibold mb-1 pr-6" style={{ color: 'var(--color-text-primary)' }}>
                                        {app.applicationName}
                                      </h4>
                                      {app.description && (
                                        <p className="text-sm mb-2 line-clamp-2" style={{ color: 'var(--color-text-muted)' }}>
                                          {app.description}
                                        </p>
                                      )}

                                      {/* Status and Version */}
                                      <div className="flex items-center gap-2 mb-3">
                                        <Badge variant={app.isActive ? 'success' : 'default'}>
                                          {app.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                        {app.version && (
                                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>v{app.version}</span>
                                        )}
                                      </div>

                                      {/* Launch Buttons */}
                                      <div className="flex gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className="flex-1"
                                          onClick={(e) => handleLaunchApp(app, e)}
                                          disabled={!app.isActive}
                                        >
                                          <RocketLaunchIcon className="h-4 w-4 mr-1" />
                                          Launch
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={() => handleLaunchAppNewTab(app)}
                                          disabled={!app.isActive}
                                          title="Open in new tab"
                                        >
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredApps && filteredApps.length === 0 && (
          <div className="text-center py-12">
            <RocketLaunchIcon className="mx-auto h-12 w-12" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>No applications found</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'There are no applications available to launch'}
            </p>
          </div>
        )}

        {/* Stats */}
        {!isLoading && applications && (
          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Showing {filteredApps.length} of {applications.length} applications
            {favoriteIds.size > 0 && ` - ${favoriteIds.size} favorited`}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LauncherPage;
